import { Form as InertiaForm } from '@inertiajs/react'
import { type ComponentProps, useState } from 'react'

import { Switch } from '@/components/ui/switch.tsx'
import { TaxModuleType } from './types.ts'
import { TAX_MODULE_ICON_OPTIONS, taxModuleIconByName } from './icons.ts'

type FormProps = Omit<ComponentProps<typeof InertiaForm>, 'children'> & {
  tax_module: TaxModuleType
  submitText: string
}

const PRESET_COLORS = [
  '#2563EB',
  '#16A34A',
  '#EA580C',
  '#4F46E5',
  '#9333EA',
  '#0891B2',
  '#DB2777',
  '#0D9488',
  '#DC2626',
  '#059669',
]

export default function Form({ tax_module, submitText, ...formProps }: FormProps) {
  const [color, setColor] = useState(tax_module.color || '#3B82F6')
  const [active, setActive] = useState(Boolean(tax_module.active))
  const [icon, setIcon] = useState(tax_module.icon || "Tag")

  function handleColorChange(nextColor : string) {
    setColor(nextColor)
  }

  return (
    <InertiaForm
      transform={data => ({ tax_module: data })}
      className="contents"
      {...formProps}
    >
      {({ errors, processing }) => (
        <>
          <div className="my-5">
            <label htmlFor="name">Nom</label>
            <input
              type="text"
              name="name"
              id="name"
              defaultValue={tax_module.name}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
            />
            {errors.name && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.name.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="color">Couleur</label>
            <div className="mt-2 space-y-3">
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((presetColor) => (
                  <button
                    key={presetColor}
                    type="button"
                    onClick={() => handleColorChange(presetColor)}
                    className={`h-7 w-7 rounded-full transition-transform hover:scale-110 ${
                      color === presetColor ? 'ring-2 ring-offset-2 ring-gray-500 scale-110' : ''
                    }`}
                    style={{ backgroundColor : presetColor }}
                    title={presetColor}
                  />
                ))}
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="h-10 w-10 cursor-pointer rounded border border-gray-400"
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="block w-full rounded-md border border-gray-400 px-3 py-2 font-mono shadow outline-none"
                  pattern="#[0-9A-Fa-f]{6}"
                  placeholder="#3B82F6"
                />
              </div>
              <input type="hidden" name="color" value={color} />
            </div>
            {errors.color && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.color.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="icon">Icône</label>
            <div className="mt-2 grid grid-cols-4 gap-2 md:grid-cols-5">
              {TAX_MODULE_ICON_OPTIONS.map((option) => {
                const Icon = taxModuleIconByName(option.value)
                const selected = icon === option.value

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setIcon(option.value)}
                    className={`flex flex-col items-center justify-center rounded-lg border p-3 text-xs transition-colors ${
                      selected ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary/60"
                    }`}
                  >
                    <Icon className="mb-1 h-5 w-5" />
                    {option.label}
                  </button>
                )
              })}
            </div>
            <input type="hidden" name="icon" value={icon} />
            {errors.icon && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.icon.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="active">Actif</label>
            <input type="hidden" name="active" value={active ? '1' : '0'} />
            <div className="mt-2 flex items-center gap-3">
                <Switch
                  id="active"
                  checked={active}
                  onCheckedChange={setActive}
                  aria-label="Basculer le statut actif"
                />
              <span className="text-sm text-gray-700">
                {active ? 'Activé' : 'Désactivé'}
              </span>
            </div>
            <p className="mt-2 text-sm text-gray-600">
              Basculez pour activer ou désactiver ce module.
            </p>
            {errors.active && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.active.join(', ')}
              </div>
            )}
          </div>

          <div className="inline">
            <button
              type="submit"
              disabled={processing}
              className="rounded-lg py-3 px-5 bg-blue-600 text-white inline-block font-medium cursor-pointer"
            >
              {submitText}
            </button>
          </div>
        </>
      )}
    </InertiaForm>
  )
}
